import type { NarrativeChapter, WhyView } from './types';
import { WHY_KEY_ROOT } from './keys';

/**
 * The WS merge, as pure functions (unit-tested), so `lib/ws/apply-event.ts`
 * stays a thin switch.
 *
 * REALTIME IS WS, NEVER POLLING (brief §6.10 / frontend-conventions §15). A
 * chapter landing mid-run arrives on the `disruption:<urn>` room that Tower and
 * Express already join, and is folded into the cache here. Nothing in this
 * feature is driven by a clock.
 */

/** Reject anything that is not structurally a chapter — a malformed push is
 *  dropped at the door rather than rendered as if it were the record. */
export function toChapter(raw: unknown): NarrativeChapter | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const c = raw as Partial<NarrativeChapter>;
  if (typeof c.chapterId !== 'string' || !c.chapterId) return null;
  if (typeof c.kind !== 'string' || typeof c.seq !== 'number') return null;
  return {
    ...(c as NarrativeChapter),
    facts: Array.isArray(c.facts) ? c.facts : [],
    // `narration` is normalised to null when it is not a real object, so a
    // mangled payload can never surface as prose.
    narration: c.narration && typeof c.narration === 'object' ? c.narration : null,
  };
}

function bySeq(a: NarrativeChapter, b: NarrativeChapter): number {
  if (a.seq !== b.seq) return a.seq - b.seq;
  return a.chapterId.localeCompare(b.chapterId);
}

/**
 * Upsert one chapter into a cached view, keyed by `chapterId`, so at-least-once
 * redelivery lands on identical state. Returns the SAME reference when the
 * chapter does not belong to this view, so unrelated caches never re-render.
 *
 * A case-scoped view holds the chapters of ITS case; a chapter naming a
 * different case is a sibling's and is ignored. A chapter with no `caseUrn`
 * (a disruption-lifecycle chapter) belongs to both.
 */
export function upsertChapter(
  view: WhyView | undefined,
  chapter: NarrativeChapter,
): WhyView | undefined {
  if (!view) return view;
  if (view.scope === 'CASE' && chapter.caseUrn && view.caseUrn && chapter.caseUrn !== view.caseUrn) {
    return view;
  }
  const at = view.chapters.findIndex((c) => c.chapterId === chapter.chapterId);
  const chapters =
    at >= 0
      ? view.chapters.map((c, i) => (i === at ? { ...c, ...chapter } : c))
      : [...view.chapters, chapter];
  return { ...view, chapters: [...chapters].sort(bySeq) };
}

/**
 * Does this cached query hold the account of `disruptionUrn`? Used as the
 * react-query filter predicate for both the disruption-scoped and the
 * case-scoped views, since a case chapter is content INSIDE the disruption's
 * account and both surfaces must flip together.
 */
export function isWhyQueryOfDisruption(
  queryKey: readonly unknown[],
  data: unknown,
  disruptionUrn: string,
): boolean {
  if (queryKey[0] !== WHY_KEY_ROOT) return false;
  const view = data as WhyView | undefined;
  return !!view && view.disruptionUrn === disruptionUrn;
}
